import { useId, useRef, useState } from 'react';

import { PHOTO_MAX_BYTES } from '@/constants';
import { PHOTO_ACCEPT } from '@/utils/helpers/media.helpers';
import type { Platform } from '@/utils/helpers/platform.helpers';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet';

const MAX_MB = Math.round(PHOTO_MAX_BYTES / (1024 * 1024));

function ImageIcon() {
  return (
    <svg
      width='20'
      height='20'
      viewBox='0 0 24 24'
      fill='none'
      stroke='#256956'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <rect x='3' y='3' width='18' height='18' rx='3' />
      <circle cx='8.5' cy='8.5' r='1.5' />
      <path d='M21 15l-5-5L5 21' />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg
      width='20'
      height='20'
      viewBox='0 0 24 24'
      fill='none'
      stroke='#256956'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <path d='M3 8h3.2l1.6-2.4h8.4L18.8 8H21a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z' />
      <circle cx='12' cy='13.5' r='3.6' />
    </svg>
  );
}

function RemoveIcon() {
  return (
    <svg
      width='15'
      height='15'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2.2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <path d='M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14' />
    </svg>
  );
}

const dropzoneClasses =
  'flex w-full cursor-pointer items-center gap-[11px] rounded-[13px] border-[1.5px] border-dashed border-[#c3c2c1] bg-[#fbfaf9] px-[15px] py-[13px] text-left';

const sourceOptionClasses =
  'flex w-full cursor-pointer items-center gap-3 rounded-[13px] border-[1.5px] border-[#e4e2dd] bg-white px-4 py-3.5 text-left text-[14.5px] font-semibold text-[#1a201d] focus-visible:border-[#256956] focus-visible:ring-[3px] focus-visible:ring-[#256956]/20 focus-visible:outline-none';

type PhotoAttachmentProps = {
  /** Object URL of the attached photo, or null for the empty state. */
  previewUrl: string | null;
  onSelect: (file: File) => void;
  onRemove: () => void;
  /** Rejection or upload message shown under the block. */
  error?: string | null;
  childFirstName?: string;
  disabled?: boolean;
  /** Drives whether we offer our own camera/gallery choice — see below. */
  platform?: Platform;
};

/**
 * "Add a photo" block under the written message (GIFTWEB-22). Text tab only —
 * the composer does not render it for Voice or Video.
 *
 * Android gets an extra step the other platforms do not (GIFTWEB-40). Safari
 * puts its own Photo Library / Take Photo / Choose File sheet in front of any
 * file input, so iOS can already reach the camera; Chrome on Android sends an
 * image-only `accept` straight to the system photo picker, which has no camera
 * entry at all. The only lever the platform gives us is `capture`, and it is
 * all-or-nothing — it forces the camera and takes the gallery away. So on
 * Android we ask first and keep one input per answer. Desktop is untouched:
 * `capture` is ignored there and a plain file dialog is what people expect.
 */
function PhotoAttachment({
  previewUrl,
  onSelect,
  onRemove,
  error,
  childFirstName,
  disabled = false,
  platform = 'other',
}: PhotoAttachmentProps) {
  const inputId = useId();
  const errorId = `${inputId}-error`;
  const [sourceOpen, setSourceOpen] = useState(false);
  const libraryRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const offerSourceChoice = platform === 'android';
  const family = childFirstName?.trim()
    ? `${childFirstName.trim()}'s family`
    : 'their family';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so picking the same file twice still fires a change event.
    e.target.value = '';
    if (file) onSelect(file);
  };

  const openPicker = (ref: React.RefObject<HTMLInputElement | null>) => {
    // Click inside the tap handler so the browser still counts this as a user
    // gesture, then dismiss. Both inputs live outside the sheet, so closing it
    // cannot unmount the one we just opened.
    ref.current?.click();
    setSourceOpen(false);
  };

  const dropzoneInner = (
    <>
      <span className='flex size-[38px] shrink-0 items-center justify-center rounded-[10px] bg-[#eef3ef]'>
        <ImageIcon />
      </span>
      <span className='min-w-0 flex-1'>
        <span className='block text-sm font-semibold text-[#1a201d]'>
          Add a photo
        </span>
        <span className='mt-px block text-xs text-[#8a938c]'>
          JPG or PNG, up to {MAX_MB}MB
        </span>
      </span>
    </>
  );

  const libraryInput = (
    <input
      ref={libraryRef}
      id={inputId}
      type='file'
      accept={PHOTO_ACCEPT}
      onChange={handleChange}
      disabled={disabled}
      aria-describedby={error ? errorId : undefined}
      data-testid='photo-input'
      className='sr-only'
    />
  );

  return (
    <div className='mt-4 border-t border-[#efece5] pt-4 text-left'>
      <div className='flex items-baseline gap-[7px]'>
        <span className='text-[13px] font-semibold text-[#2a2928]'>
          Add a photo
        </span>
        <span className='text-xs text-[#8a938c]'>Optional</span>
      </div>
      <p className='mt-1 mb-2.5 text-[12.5px] leading-[1.45] text-[#6b736e]'>
        A picture to go with your note, saved with your message for {family}.
      </p>

      {previewUrl ? (
        <div
          data-testid='photo-attached'
          className='flex w-full items-center gap-3 rounded-[13px] border-[1.5px] border-[#d9e6df] bg-white px-3 py-2.5'
        >
          <img
            src={previewUrl}
            alt='Attached to your message'
            className='size-12 shrink-0 rounded-[10px] object-cover'
          />
          <div className='min-w-0 flex-1'>
            <p className='text-[13.5px] font-semibold text-[#1a201d]'>
              Photo added
            </p>
            <p className='mt-px text-xs text-[#6a8b7f]'>Looks lovely.</p>
          </div>
          <button
            type='button'
            onClick={onRemove}
            disabled={disabled}
            aria-label='Remove photo'
            data-testid='photo-remove'
            className='inline-flex shrink-0 cursor-pointer items-center gap-1.5 border-none bg-transparent p-0 text-[12.5px] font-bold text-[#9f1239] disabled:cursor-not-allowed disabled:opacity-60'
          >
            <RemoveIcon />
            Remove
          </button>
        </div>
      ) : offerSourceChoice ? (
        <>
          <button
            type='button'
            onClick={() => setSourceOpen(true)}
            disabled={disabled}
            data-testid='photo-dropzone'
            className={`${dropzoneClasses} focus-visible:border-[#256956] focus-visible:ring-[3px] focus-visible:ring-[#256956]/20 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60`}
          >
            {dropzoneInner}
          </button>

          {libraryInput}
          <input
            ref={cameraRef}
            type='file'
            accept={PHOTO_ACCEPT}
            capture='environment'
            onChange={handleChange}
            disabled={disabled}
            data-testid='photo-camera-input'
            className='sr-only'
          />

          <Sheet open={sourceOpen} onOpenChange={setSourceOpen}>
            <SheetContent
              data-testid='photo-source-sheet'
              showCloseButton={false}
              className='gap-3'
              // Radix would focus the first option, which on a touch sheet
              // reads as "already chosen". Hold focus on the panel instead:
              // the dialog stays keyboard-reachable and ESC still closes it.
              onOpenAutoFocus={(e) => {
                e.preventDefault();
                (e.currentTarget as HTMLElement | null)?.focus();
              }}
            >
              <SheetTitle className='text-lg leading-tight font-bold tracking-[-0.01em] text-[#1a201d]'>
                Add a photo
              </SheetTitle>
              <SheetDescription className='text-[13.5px] leading-[1.5] text-[#4a534d]'>
                Take a new one, or pick something already on your phone.
              </SheetDescription>

              <div className='flex flex-col gap-2'>
                <button
                  type='button'
                  onClick={() => openPicker(cameraRef)}
                  data-testid='photo-source-camera'
                  className={sourceOptionClasses}
                >
                  <CameraIcon />
                  Take photo
                </button>
                <button
                  type='button'
                  onClick={() => openPicker(libraryRef)}
                  data-testid='photo-source-library'
                  className={sourceOptionClasses}
                >
                  <ImageIcon />
                  Choose from gallery
                </button>
              </div>

              <SheetClose
                data-testid='photo-source-cancel'
                className='mt-1 cursor-pointer border-none bg-transparent py-2 text-[13.5px] font-semibold text-[#6b736e]'
              >
                Cancel
              </SheetClose>
            </SheetContent>
          </Sheet>
        </>
      ) : (
        <label
          htmlFor={inputId}
          data-testid='photo-dropzone'
          className={`${dropzoneClasses} focus-within:border-[#256956] focus-within:ring-[3px] focus-within:ring-[#256956]/20`}
        >
          {dropzoneInner}
          {libraryInput}
        </label>
      )}

      {error && (
        <p
          id={errorId}
          role='alert'
          data-testid='photo-error'
          className='mt-[7px] rounded-[13px] border border-[#fecdd3] bg-[#fff1f2] px-3.5 py-2.5 text-[12.5px] leading-[1.45] text-[#9f1239]'
        >
          {error}
        </p>
      )}
    </div>
  );
}

export default PhotoAttachment;
